import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { useSound } from '../hooks/useSound';
import { useLanguage } from '../contexts/LanguageContext';
import { School, BarChart3, Target } from 'lucide-react';
import gsap from 'gsap';
import axios from 'axios';

const MODES = [
  { key: 'school', labelKey: 'browse.schoolYear', icon: School, descKey: 'browse.byGradeMonth' },
  { key: 'level', labelKey: 'browse.byLevel', icon: BarChart3, descKey: 'browse.cefrLevels' },
  { key: 'topic', labelKey: 'browse.byTopic', icon: Target, descKey: 'browse.pickTheme' },
];

export function BrowseHome() {
  const navigate = useNavigate();
  const { playSound } = useSound();
  const { t } = useLanguage();
  const [mode, setMode] = useState(() => localStorage.getItem('browseMode') || 'level');
  const [grades, setGrades] = useState([]);
  const [levels, setLevels] = useState([]);
  const [topics, setTopics] = useState([]);
  const titleRef = useRef(null);
  const cardsRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/grades`).then(r => setGrades(r.data)).catch(console.error);
    axios.get(`${API}/levels`).then(r => setLevels(r.data)).catch(console.error);
    axios.get(`${API}/topics`).then(r => setTopics(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    localStorage.setItem('browseMode', mode);
  }, [mode]);

  useEffect(() => {
    if (cardsRef.current?.children?.length > 0) {
      gsap.fromTo(cardsRef.current.children,
        { y: 30, opacity: 0, scale: 0.92 },
        { y: 0, opacity: 1, scale: 1, duration: 0.35, stagger: 0.04, ease: 'back.out(1.5)' }
      );
    }
  }, [mode, grades, levels, topics]);

  useEffect(() => {
    const tl = gsap.timeline();
    if (titleRef.current) tl.fromTo(titleRef.current, { y: -30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' });
    if (toggleRef.current) tl.fromTo(toggleRef.current, { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.3 }, '-=0.2');
  }, []);

  const handleModeSwitch = (newMode) => {
    playSound('click');
    setMode(newMode);
  };

  const GRADE_STYLES = [
    { bg: '#E03131', border: '#C92A2A' },
    { bg: '#1971C2', border: '#1864AB' },
    { bg: '#F59F00', border: '#E67700' },
  ];

  const LEVEL_STYLES = {
    'pre-A1': { bg: '#2F9E44', border: '#237B34' },
    'A1': { bg: '#1971C2', border: '#1864AB' },
    'A1-A2': { bg: '#F59F00', border: '#E67700' },
    'A2': { bg: '#E8590C', border: '#D9480F' },
  };

  const TOPIC_COLORS = [
    '#E03131', '#1971C2', '#2F9E44', '#7048E8', '#F59F00',
    '#E8590C', '#0B7285', '#862E9C', '#D6336C', '#5C940D',
    '#1098AD', '#845EF7', '#E64980', '#15AABF', '#FF6B6B',
    '#4C6EF5', '#20C997',
  ];

  return (
    <div data-testid="browse-home-screen" style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #E7F5FF 0%, #F0F4F8 50%, #FFF5F5 100%)',
    }}>
      {/* Header */}
      <div ref={titleRef} style={{ textAlign: 'center', paddingTop: '1.5rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.25rem' }}>&#x1F1E8;&#x1F1E6;</div>
        <h1 style={{
          fontFamily: 'Fredoka, sans-serif',
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1,
        }}>
          FrenchBoard
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', marginTop: '0.25rem', fontWeight: 600 }}>
          {t('browse.subtitle')}
        </p>
      </div>

      {/* Mode Toggle */}
      <div ref={toggleRef} style={{ display: 'flex', justifyContent: 'center', padding: '1rem 1rem 0.75rem' }}>
        <div data-testid="browse-mode-toggle" style={{
          display: 'inline-flex', gap: '4px', padding: '4px',
          background: 'var(--surface)', borderRadius: '1rem',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08), inset 0 1px 2px rgba(0,0,0,0.04)',
          border: '2px solid var(--border)',
        }}>
          {MODES.map(m => {
            const active = mode === m.key;
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                data-testid={`mode-${m.key}-button`}
                onClick={() => handleModeSwitch(m.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.6rem 1.25rem', borderRadius: '0.75rem',
                  border: 'none', cursor: 'pointer',
                  background: active ? 'var(--primary)' : 'transparent',
                  color: active ? 'white' : 'var(--text-secondary)',
                  fontFamily: 'Fredoka, sans-serif', fontSize: '1.05rem', fontWeight: 600,
                  transition: 'all 0.2s ease',
                  boxShadow: active ? '0 2px 8px rgba(76,110,245,0.3)' : 'none',
                }}
              >
                <Icon size={18} />
                <span>{t(m.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem 2rem 2rem' }}>
        {/* School Year Mode */}
        {mode === 'school' && (
          <div>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '1rem', marginBottom: '1.25rem' }}>
              {t('browse.selectGrade')}
            </p>
            <div ref={cardsRef} style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              {grades.map((g, i) => {
                const s = GRADE_STYLES[i];
                return (
                  <button key={g.grade} data-testid={`grade-${g.grade}-button`}
                    onClick={() => { playSound('click'); navigate(`/school/${g.grade}`); }}
                    style={{
                      minWidth: 200, minHeight: 160, padding: '1.5rem 2.5rem',
                      borderRadius: '1.5rem', border: 'none', borderBottom: `8px solid ${s.border}`,
                      background: s.bg, color: 'white', cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
                      boxShadow: `0 8px 24px ${s.bg}40`,
                      touchAction: 'manipulation', transition: 'transform 0.1s',
                    }}
                    onMouseDown={e => e.currentTarget.style.transform = 'translateY(4px)'}
                    onMouseUp={e => e.currentTarget.style.transform = ''}
                  >
                    <span className="font-score" style={{ fontSize: '4rem', fontWeight: 700, lineHeight: 1 }}>{g.grade}</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 600, opacity: 0.9 }}>{t('browse.grade')} {g.grade}</span>
                    <span style={{ fontSize: '1rem', opacity: 0.75 }}>{g.months} {t('browse.months')}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* By Level Mode */}
        {mode === 'level' && (
          <div>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '1rem', marginBottom: '1.25rem' }}>
              {t('browse.selectLevel')}
            </p>
            <div ref={cardsRef} style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', justifyContent: 'center', maxWidth: 1000, margin: '0 auto' }}>
              {levels.map((l) => {
                const s = LEVEL_STYLES[l.level] || { bg: '#868E96', border: '#495057' };
                return (
                  <button key={l.level} data-testid={`level-${l.level}-button`}
                    onClick={() => { playSound('click'); navigate(`/level/${encodeURIComponent(l.level)}`); }}
                    style={{
                      flex: '1 1 200px', maxWidth: 240, minHeight: 180, padding: '1.25rem 1.5rem',
                      borderRadius: '1.5rem', border: 'none', borderBottom: `8px solid ${s.border}`,
                      background: s.bg, color: 'white', cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                      boxShadow: `0 8px 24px ${s.bg}40`,
                      touchAction: 'manipulation', transition: 'transform 0.1s',
                    }}
                    onMouseDown={e => e.currentTarget.style.transform = 'translateY(4px)'}
                    onMouseUp={e => e.currentTarget.style.transform = ''}
                  >
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {l.level}
                    </span>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.2 }}>
                      {t(`level.${l.level}`) !== `level.${l.level}` ? t(`level.${l.level}`) : l.label}
                    </span>
                    <span style={{ fontSize: '1rem', opacity: 0.85, fontStyle: 'italic' }}>
                      {l.subtitle}
                    </span>
                    <span style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '0.25rem' }}>
                      {l.item_count.toLocaleString()} {t('browse.items')}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* By Topic Mode */}
        {mode === 'topic' && (
          <div>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '1rem', marginBottom: '1.25rem' }}>
              {t('browse.pickTopic')}
            </p>
            <div ref={cardsRef} style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '1rem', maxWidth: 1200, margin: '0 auto',
            }}>
              {topics.map((tp, i) => (
                <button key={tp.topic} data-testid={`topic-${tp.topic}-button`}
                  onClick={() => { playSound('click'); navigate(`/topic/${tp.topic}`); }}
                  style={{
                    background: 'var(--surface)', border: '3px solid var(--border)',
                    borderRadius: '1.25rem', padding: '1.25rem', cursor: 'pointer',
                    textAlign: 'left', transition: 'all 0.15s',
                    borderLeft: `6px solid ${TOPIC_COLORS[i % TOPIC_COLORS.length]}`,
                  }}
                >
                  <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                    {t(`topic.${tp.topic}`) !== `topic.${tp.topic}` ? t(`topic.${tp.topic}`) : tp.label}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                    {tp.item_count} {t('browse.items')} &middot; {tp.game_count} {t('browse.games')}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
